// src/containers/pages/caseManagement/components/TableToolbar.tsx

import React from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Tooltip,
  Paper,
} from 'components/mui';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import ReplayIcon from '@mui/icons-material/Replay';
import type { Employee, MoveProgress } from '../types';

interface TableToolbarProps {
  // Employee filter
  employees: Employee[];
  selectedEmployeeId: number | null;
  onEmployeeFilter: (consultantId: number | null) => void;

  // Counts
  totalComments: number;
  filteredCount: number;
  selectedCount: number;

  // Move validation
  canMove: boolean;
  unmappedSelectedCount: number;
  hasDestinationCase: boolean;

  // Execution state
  applying: boolean;
  progress: MoveProgress;
  failedCount: number;

  // Actions
  onMove: () => void;
  onRetryFailed: () => void;
}

function TableToolbar({
  employees,
  selectedEmployeeId,
  onEmployeeFilter,
  totalComments,
  filteredCount,
  selectedCount,
  canMove,
  unmappedSelectedCount,
  applying,
  progress,
  failedCount,
  hasDestinationCase,
  onMove,
  onRetryFailed,
}: TableToolbarProps) {
  // Build tooltip for disabled move button
  const getMoveTooltip = (): string => {
    if (applying) return 'Move in progress...';
    if (selectedCount === 0) return 'Select at least one activity to move';
    if (!hasDestinationCase) return 'Select a destination case first';
    if (unmappedSelectedCount > 0)
      return `${unmappedSelectedCount} selected activity(ies) need a destination activity mapped`;
    return '';
  };

  const moveTooltip = getMoveTooltip();
  const progressPercent =
    progress.total > 0 ? (progress.done / progress.total) * 100 : 0;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      {/* Progress bar -- shown only during execution */}
      {applying && (
        <Box sx={{ width: '100%', mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, textAlign: 'center' }}
          >
            Moving {progress.done} of {progress.total} activities...
            {progress.failed > 0 && (
              <Typography
                component="span"
                variant="body2"
                color="error.main"
                sx={{ ml: 1 }}
              >
                ({progress.failed} failed)
              </Typography>
            )}
          </Typography>
        </Box>
      )}

      {/* Main toolbar row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Left: Employee filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="employee-filter-label">Employee</InputLabel>
          <Select
            labelId="employee-filter-label"
            value={selectedEmployeeId ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onEmployeeFilter(val === '' ? null : Number(val));
            }}
            label="Employee"
            disabled={applying}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Center: Counts */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Showing {filteredCount} of {totalComments} activities
          </Typography>
          {selectedCount > 0 && (
            <Typography variant="subtitle2" color="primary.main">
              {selectedCount} selected
            </Typography>
          )}
        </Box>

        {/* Right: Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Retry Failed button -- shown only after partial failure */}
          {failedCount > 0 && !applying && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<ReplayIcon />}
              onClick={onRetryFailed}
            >
              Retry Failed ({failedCount})
            </Button>
          )}

          {/* Move button */}
          <Tooltip
            title={moveTooltip}
            disableHoverListener={canMove}
            arrow
          >
            {/* Wrapping in span so Tooltip works on disabled button */}
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<MoveDownIcon />}
                onClick={onMove}
                disabled={!canMove}
                sx={{ minWidth: 100 }}
              >
                {applying ? 'Moving...' : 'MOVE'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
}

export default TableToolbar;
